/**
 * <read-more-content>
 *
 * Collapses long rich-text content to a word budget and reveals the rest behind an
 * inline Read more toggle.
 *
 * Rules:
 *   1. Only literal words count. Tags, attributes, URLs and alt text contribute nothing.
 *   2. Plain text is cut exactly at the budget, mid-paragraph if that is where the word
 *      falls. Cutting only at paragraph boundaries left most of this catalogue untouched,
 *      because descriptions are written as two or three very long paragraphs.
 *   3. Elements in ATOMIC are never split. If one would cross the budget the cut moves to
 *      just BEFORE it, so a long link starting near the boundary cannot drag the whole
 *      anchor into view. The element is only kept when nothing is visible yet, which
 *      stops the collapsed panel rendering empty.
 *   4. Media carries a notional word cost. An image contributes no text but occupies a
 *      lot of vertical space, so counting it as zero let image-heavy descriptions stay
 *      enormous while technically sitting inside the budget.
 *
 * Expanding is one-way: the toggle removes itself and the content stays open. There is no
 * Read less. Putting one back means finding somewhere for it to sit once the content is
 * open, and every option is worse than leaving the reader with what they asked to see —
 * inline at the cut it ends up buried mid-paragraph, and at the end of the last paragraph
 * its 44px tap target inflates that line box and pushes the final line down.
 *
 * Nothing is removed from the DOM. The tail is hidden by class, so expanding restores the
 * original nodes and any listeners on them. If this script never runs the content stays
 * fully visible, so a failure degrades to "everything shown" rather than "content lost".
 */
const ATOMIC = ['A', 'IMG', 'PICTURE', 'IFRAME', 'VIDEO', 'SVG'];
const MEDIA = ['IMG', 'PICTURE', 'IFRAME', 'VIDEO', 'SVG'];
const MEDIA_WORD_COST = 10;

class ReadMoreContent extends HTMLElement {
  connectedCallback() {
    this.wordBudget = parseInt(this.dataset.words, 10) || 70;
    this.hiddenNodes = [];
    this.words = 0;
    this.cutNode = null;
    this.cutOffset = 0;
    this.cutBefore = null;

    if (!this.findCut(this)) return;

    const tail = this.splitAtCut();
    if (!tail) return;

    this.marker = document.createElement('span');
    this.marker.className = 'read-more__marker';
    tail.parentNode.insertBefore(this.marker, tail);

    this.hideFrom(tail);
    if (this.hiddenNodes.length === 0) {
      this.marker.remove();
      return;
    }

    this.markTailBlock();
    this.build();
  }

  /**
   * Flags the block the cut landed in, which is the last one still on screen while
   * collapsed. Its bottom margin is meant for a paragraph that has another below it, and
   * with the rest hidden it becomes trailing space the expanded state does not have.
   */
  markTailBlock() {
    let node = this.marker;

    while (node.parentNode && node.parentNode !== this) node = node.parentNode;

    if (node.nodeType === Node.ELEMENT_NODE) {
      this.tailBlock = node;
      node.classList.add('read-more__tail-block');
    }
  }

  /**
   * Walks the tree in document order accumulating literal words, recording where the
   * budget is reached.
   * @param {Node} root
   * @returns {boolean} True once the cut point has been found.
   */
  findCut(root) {
    for (const child of Array.from(root.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        if (ATOMIC.includes(child.tagName)) {
          const cost = this.costOf(child);

          // Cut before it rather than swallowing it whole, unless there is nothing
          // visible yet to cut back to.
          if (this.words > 0 && this.words + cost >= this.wordBudget) {
            this.cutBefore = child;
            return true;
          }

          this.words += cost;
        } else if (this.findCut(child)) {
          return true;
        }
      } else if (child.nodeType === Node.TEXT_NODE) {
        const match = this.wordEndOffset(child.data);
        if (match !== null) {
          this.cutNode = child;
          this.cutOffset = match;
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Counts words in a text string, returning the character offset just past the word
   * that reaches the budget.
   * @param {string} text
   * @returns {number|null} Offset, or null if the budget is not reached in this text.
   */
  wordEndOffset(text) {
    const pattern = /\S+/g;
    let match = pattern.exec(text);

    while (match !== null) {
      this.words += 1;
      if (this.words >= this.wordBudget) return match.index + match[0].length;
      match = pattern.exec(text);
    }

    return null;
  }

  /**
   * @param {Element} element
   * @returns {number}
   */
  countWords(element) {
    return (element.textContent || '').trim().split(/\s+/).filter(Boolean).length;
  }

  /**
   * Budget cost of an atomic element: its own words plus a notional cost for any media
   * it is or contains, so a link wrapping an image is not treated as free.
   * @param {Element} element
   * @returns {number}
   */
  costOf(element) {
    const mediaCount = MEDIA.includes(element.tagName)
      ? 1
      : element.querySelectorAll(MEDIA.join(',')).length;

    return this.countWords(element) + mediaCount * MEDIA_WORD_COST;
  }

  /**
   * Splits the text node at the cut so the tail can be hidden separately.
   * @returns {Node|null} First node of the hidden tail.
   */
  splitAtCut() {
    if (this.cutBefore) return this.cutBefore;
    if (!this.cutNode) return null;

    return this.cutOffset >= this.cutNode.data.length
      ? this.cutNode.nextSibling
      : this.cutNode.splitText(this.cutOffset);
  }

  /**
   * Hides the node and everything after it in document order, walking up to this element.
   * Ancestors themselves stay visible because they still hold visible content.
   * @param {Node} startNode
   */
  hideFrom(startNode) {
    let node = startNode;

    while (node) {
      // Captured before marking, because wrapping a text node changes its parent.
      const parent = node.parentNode;
      let sibling = node;

      while (sibling) {
        const next = sibling.nextSibling;
        this.markHidden(sibling);
        sibling = next;
      }

      if (!parent || parent === this) break;

      // Climb to the nearest ancestor that still has content after it. Reading
      // parent.nextSibling directly abandons the rest of the document whenever the cut
      // lands inside the last child of a wrapper — the common case, since a trailing
      // <img> is usually the last node in its paragraph.
      node = null;
      for (let ancestor = parent; ancestor && ancestor !== this; ancestor = ancestor.parentNode) {
        if (ancestor.nextSibling) {
          node = ancestor.nextSibling;
          break;
        }
      }
    }
  }

  /**
   * @param {Node} node
   */
  markHidden(node) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      node.classList.add('read-more__hidden');
      this.hiddenNodes.push(node);
      return;
    }

    if (node.nodeType !== Node.TEXT_NODE || !node.data.trim()) return;

    const wrapper = document.createElement('span');
    wrapper.className = 'read-more__hidden';
    node.parentNode.insertBefore(wrapper, node);
    wrapper.append(node);
    this.hiddenNodes.push(wrapper);
  }

  /**
   * Builds the ellipsis and toggle and places them at the cut, so the toggle trails the
   * last visible word rather than starting a row of its own.
   */
  build() {
    const ellipsis = document.createElement('span');
    ellipsis.className = 'read-more__ellipsis';
    ellipsis.textContent = ' … ';
    ellipsis.setAttribute('aria-hidden', 'true');

    this.toggle = document.createElement('button');
    this.toggle.type = 'button';
    this.toggle.className = 'read-more__toggle';
    this.toggle.textContent = this.dataset.moreLabel;
    this.toggle.setAttribute('aria-expanded', 'false');

    this.hiddenNodes.forEach((node, index) => {
      if (!node.id) node.id = `${this.dataset.blockId}-more-${index}`;
    });
    this.toggle.setAttribute(
      'aria-controls',
      this.hiddenNodes.map((node) => node.id).join(' ')
    );

    this.toggle.addEventListener('click', this.expand.bind(this), { once: true });
    this.marker.append(ellipsis, this.toggle);
  }

  /**
   * Reveals the tail and takes the control away with it.
   */
  expand() {
    const hadFocus = this.contains(document.activeElement);

    this.hiddenNodes.forEach((node) => node.classList.remove('read-more__hidden'));
    if (this.tailBlock) this.tailBlock.classList.remove('read-more__tail-block');
    this.marker.remove();

    // The button the reader just activated no longer exists, so focus would drop to the
    // body and a keyboard reader would restart from the top of the page. Park it on the
    // content that was revealed instead.
    if (hadFocus) {
      this.setAttribute('tabindex', '-1');
      this.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('read-more-content')) {
  customElements.define('read-more-content', ReadMoreContent);
}
